import type { AutocompleteProps } from '@magicschool/spellbook/components/Autocomplete';
import { useAutocomplete } from '@magicschool/spellbook/components/Autocomplete/AutocompleteProvider';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import type { LabelProps } from '@magicschool/spellbook/components/Label';
import { Label } from '@magicschool/spellbook/components/Label';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { Text } from '@magicschool/spellbook/components/Text';
import { useAutocompleteHighlightedLabel } from '@magicschool/spellbook/hooks/useAutocompleteHighlightedLabel';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { SEARCH_OVERRIDE_VALUE } from '@magicschool/spellbook/lib/autocomplete';
import type { SelectOption } from '@magicschool/spellbook/lib/select';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type * as PopoverPrimitive from 'radix-ui/popover';
import { Children, forwardRef, useEffect, useMemo, useRef, useState } from 'react';

export type AutocompleteTriggerProps = React.ComponentProps<typeof PopoverPrimitive.Trigger> &
  Pick<
    AutocompleteProps,
    'placeholder' | 'name' | 'readOnly' | 'labelId' | 'size' | 'id' | 'invalid' | 'disabled' | 'data-testid' | 'withShadow'
  > & {
    comboboxId?: string;
    contentId?: string;
    /** Sets `aria-required` on the combobox input; the native `required` cannot apply to the hidden value input */
    required?: boolean;
  };

export type AutocompleteContentProps = React.ComponentPropsWithoutRef<'div'> &
  Pick<AutocompleteProps, 'descriptionOrientation' | 'disabled' | 'isLoading' | 'size'>;

export type AutocompleteOptionProps = React.ComponentPropsWithoutRef<'div'> &
  SelectOption & {
    disableTextHighlighting?: boolean;
    searchString?: string;
  } & Pick<AutocompleteProps, 'size'>;

export type AutocompleteGroupProps = React.ComponentPropsWithoutRef<'div'> & {
  label: string;
} & Pick<AutocompleteProps, 'size'>;

const optionVariants = cva(['flex', 'items-center', 'justify-between', 'outline-none', 'py-2', 'gap-2', 'group/autocomplete-option'], {
  defaultVariants: {
    disabled: false,
  },
  variants: {
    size: {
      md: 'px-2',
      lg: 'px-3',
      xl: 'px-3',
    },
    disabled: {
      true: 'cursor-not-allowed bg-transparent',
      false: [
        'cursor-pointer',
        'data-[selected=true]:bg-selected-default-background',
        'data-[selected=true]:hover:bg-selected-default-background-hover',
        'data-[selected=true]:active:bg-selected-default-background-active',
        'data-[selected=true]:focus-visible:bg-selected-default-background-hover',
        '[&:not(:focus-visible):hover]:bg-surface-overlay-1',
        'focus-visible:bg-surface-overlay-1',
        'data-[highlighted=true]:bg-surface-overlay-1',
      ],
    },
  },
});

const optionLabelVariants = cva(
  [
    'flex',
    'overflow-hidden',
    'text-default-text-strong',
    'me-4',
    /**
     * @TODO [SPE-16] Centralize "Selected" Color Tokens into Shared Style Utilities
     * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
     */
    'group-data-[disabled=true]/autocomplete-option:text-disabled-foreground',
    'group-data-[disabled=true]/autocomplete-option:cursor-not-allowed',
    'group-data-[disabled=false]/autocomplete-option:text-default-text-strong',
    'group-data-[disabled=false]/autocomplete-option:group-data-[selected=true]/autocomplete-option:text-selected-default-foreground',
    'group-data-[disabled=false]/autocomplete-option:group-data-[selected=true]/autocomplete-option:group-hover/autocomplete-option:text-selected-default-foreground-hover',
    'group-data-[disabled=false]/autocomplete-option:group-data-[selected=true]/autocomplete-option:group-focus-visible/autocomplete-option:text-selected-default-foreground-hover',
    'group-data-[disabled=false]/autocomplete-option:group-data-[selected=true]/autocomplete-option:group-active/autocomplete-option:text-selected-default-foreground-active',
  ],
  {
    variants: {
      descriptionOrientation: {
        horizontal: 'flex-row gap-2 items-baseline [&_span]:truncate',
        vertical: 'flex-col grow-1 gap-0 items-start',
      },
    },
  },
);

const triggerButtonVariants = cva('w-full outline-none text-sm cursor-text disabled:cursor-not-allowed', {
  variants: {
    size: {
      md: 'h-control-h-md rounded-control-md',
      lg: 'h-control-h-lg rounded-control-lg',
      xl: 'h-control-h-xl rounded-control-xl',
    },
  },
});

const triggerWrapperVariants = cva(
  [
    'flex',
    'items-center',
    'justify-start',
    'w-full',
    'border',
    'text-sm',
    'font-normal',
    'transition-all',
    'outline-none',
    'text-transparent',
    'absolute',
  ],
  {
    variants: {
      size: {
        md: 'h-control-h-md rounded-control-md',
        lg: 'h-control-h-lg rounded-control-lg',
        xl: 'h-control-h-xl rounded-control-xl',
      },
      readOnly: {
        true: 'border-surface-background-1 bg-surface-background-1',
        false: [
          'border-border-strong',
          'bg-surface-background-0',
          'group-hover/autocomplete:border-border-strong-hover',
          'group-hover/autocomplete:focus-within:border-focus-ring',
          'group-focus-within/autocomplete:border-focus-ring',
          'focus-within:border-focus-ring',
          'data-[state=open]:border-focus-ring',
        ],
      },
      disabled: {
        true: ['cursor-not-allowed', '!border-disabled-background', '!bg-disabled-background', 'text-disabled-foreground'],
      },
      invalid: {
        true: [
          /**
           * @TODO [SPE-105] Centralize "invalid"/state color tokens to single utility
           * https://linear.app/magicschool/issue/SPE-105/centralize-invalid-state-color-tokens
           */
          'border-error-stroke',
          'hover:border-error-stroke',
          'focus-within:border-error-stroke',
          'hover:focus-within:border-error-stroke',
          'data-[state=open]:border-error-stroke',
          'has-[[data-state=open]]:border-error-stroke',
          'group-focus-within/autocomplete:border-error-stroke',
        ],
      },
      withShadow: {
        true: 'shadow-card group-focus-within/autocomplete:shadow-card-hover hover:shadow-card-hover hover:focus-within:shadow-card-hover',
        false: '',
      },
    },
    defaultVariants: {
      readOnly: false,
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'border-disabled-background hover:border-disabled-background focus-within:border-disabled-background',
      },
      {
        invalid: true,
        readOnly: true,
        className: 'border-error-stroke hover:border-error-stroke focus-within:border-error-stroke',
      },
      {
        readOnly: true,
        disabled: true,
        className: 'border-disabled-background bg-disabled-background',
      },
    ],
  },
);

const triggerLeadingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-r-1',
    'ltr:border-r-border-strong',
    'rtl:border-l-1',
    'rtl:border-l-border-strong',
  ],
  {
    variants: {
      size: {
        md: 'px-2',
        lg: 'px-3',
        xl: 'px-3',
      },
      disabled: {
        true: 'ltr:border-r-disabled-background rtl:border-l-disabled-background',
      },
    },
  },
);

const triggerTrailingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-l-1',
    'ltr:border-l-border-strong',
    'rtl:border-r-1',
    'rtl:border-r-border-strong',
  ],
  {
    variants: {
      size: {
        md: 'px-2',
        lg: 'px-3',
        xl: 'px-3',
      },
      disabled: {
        true: 'ltr:border-l-disabled-background rtl:border-r-disabled-background',
      },
    },
  },
);

const triggerInputVariants = cva(
  ['cursor-text', 'focus-visible:outline-0', 'w-full', 'h-full', 'bg-transparent', 'transition-colors', 'border-0', 'relative'],
  {
    variants: {
      size: {
        md: 'h-control-h-sm px-2',
        lg: 'h-control-h-md px-3',
        xl: 'h-control-h-lg px-3 gap-2',
      },
      disabled: {
        true: 'placeholder:text-disabled-foreground text-disabled-foreground',
        false: 'text-default-text-strong placeholder:text-placeholder group-hover/autocomplete:placeholder:text-placeholder-hover',
      },
      readOnly: {
        true: 'cursor-text placeholder:text-disabled-foreground',
        false: '',
      },
    },
    defaultVariants: {
      disabled: false,
      readOnly: false,
    },
    compoundVariants: [
      {
        disabled: true,
        readOnly: true,
        className: 'placeholder:text-disabled-foreground',
      },
    ],
  },
);

const triggerAdornmentVariants = cva('flex-0', {
  variants: {
    size: {
      md: 'ltr:pl-2 rtl:pr-2 ltr:-mr-1 rtl:-ml-1',
      lg: 'ltr:pl-3 rtl:pr-3 ltr:-mr-1 rtl:-ml-1',
      xl: 'ltr:pl-3 rtl:pr-3 ltr:-mr-1 rtl:-ml-1',
    },
  },
});

const triggerIconVariants = cva(
  [
    'flex-0',
    'absolute',
    'top-1/2',
    'transform',
    '-translate-y-1/2',
    'text-default-icon',
    'group-focus-within/autocomplete:text-primary-stroke',
  ],
  {
    variants: {
      size: {
        md: 'ltr:right-2 rtl:left-2',
        lg: 'ltr:right-3 rtl:left-3',
        xl: 'ltr:right-3 rtl:left-3',
      },
      disabled: {
        true: 'text-disabled-foreground group-focus-within/autocomplete:text-disabled-foreground',
        false: 'text-default-icon group-focus-within/autocomplete:text-primary-icon',
      },
      invalid: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      disabled: false,
      invalid: false,
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: false,
        className: 'text-error-icon group-focus-within/autocomplete:text-error-icon',
      },
      {
        invalid: true,
        disabled: true,
        className: 'text-disabled-foreground group-focus-within/autocomplete:text-disabled-foreground',
      },
    ],
  },
);

const trailingAdornmentIconVariants = cva('m-1', {
  variants: {
    type: {
      select: 'transform',
      search: '',
      text: '',
    },
    open: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      type: 'select',
      open: true,
      className: 'rotate-180',
    },
  ],
});

export const AutocompleteTriggerSlot = Slot<AutocompleteTriggerProps>({
  displayName: 'Autocomplete.Trigger',
  isRenderedByDefault: true,
  renderDefault: (props) => {
    const AutocompleteTrigger: React.FC<AutocompleteTriggerProps> = useMemo(
      () =>
        forwardRef(
          (
            {
              'data-testid': dataTestId,
              disabled,
              id,
              labelId,
              contentId,
              comboboxId,
              invalid,
              placeholder,
              readOnly,
              size,
              children,
              withShadow,
              required,
              'aria-describedby': ariaDescribedBy,
            },
            ref,
          ) => {
            const {
              inputRef,
              handleInputOnBlur,
              handleInputOnChange,
              handleInputOnClick,
              handleInputOnClear,
              handleInputOnFocus,
              handleInputOnKeyDown,
              handleTriggerOnBlur,
              handleTriggerOnClick,
              handleTriggerOnFocus,
              open,
              label,
              searchString,
              text = {},
              type,
            } = useAutocomplete();
            const { clearSearch = 'Clear' } = text;

            const { slots } = useSlots(children, {
              leadingAddOn: { Component: AutocompleteLeadingAddOnSlot },
              leadingAdornment: { Component: AutocompleteLeadingAdornmentSlot, props: { ...(disabled && { color: 'disabled' }), size } },
              trailingAddOn: { Component: AutocompleteTrailingAddOnSlot },
              trailingAdornment: { Component: AutocompleteTrailingAdornmentSlot, props: { open, readOnly, disabled, size, type } },
            });

            const inputType = useMemo(() => {
              if (type === 'search') {
                return 'search';
              }
              return 'text';
            }, [type]);

            return (
              <div
                data-spellbook-name="autocomplete-trigger"
                className="flex items-center relative w-full max-w-none sm:min-w-form-field-min group/autocomplete"
              >
                <button
                  type="button"
                  className={triggerButtonVariants({ size })}
                  data-testid={dataTestId}
                  id={id}
                  onClick={handleTriggerOnClick}
                  onDoubleClick={handleTriggerOnClick}
                  onFocus={handleTriggerOnFocus}
                  onBlur={handleTriggerOnBlur}
                  ref={ref}
                  disabled={disabled}
                  tabIndex={-1}
                >
                  {placeholder}
                </button>
                <span
                  className={cn(triggerWrapperVariants({ size, readOnly, disabled, invalid, withShadow }))}
                  data-state={open ? 'open' : 'closed'}
                >
                  {slots.leadingAddOn && <span className={triggerLeadingAddOnVariants({ size, disabled })}>{slots.leadingAddOn}</span>}
                  <span className="w-full h-full relative flex items-center">
                    {slots.leadingAdornment && <span className={triggerAdornmentVariants({ size })}>{slots.leadingAdornment}</span>}
                    <input
                      aria-invalid={invalid ? 'true' : undefined}
                      aria-label={placeholder}
                      aria-haspopup="listbox"
                      aria-autocomplete="list"
                      aria-controls={contentId}
                      autoComplete="off"
                      role="combobox"
                      id={comboboxId}
                      aria-expanded={open ? 'true' : 'false'}
                      aria-required={required || undefined}
                      aria-describedby={ariaDescribedBy}
                      {...(labelId && { 'aria-labelledby': labelId })}
                      className={triggerInputVariants({ size, disabled, readOnly })}
                      type={inputType}
                      value={label || searchString}
                      onChange={handleInputOnChange}
                      onKeyDown={handleInputOnKeyDown}
                      onClick={handleInputOnClick}
                      onFocus={handleInputOnFocus}
                      onBlur={handleInputOnBlur}
                      placeholder={placeholder}
                      readOnly={readOnly}
                      disabled={disabled}
                      ref={inputRef}
                    />

                    {slots.trailingAdornment && (
                      <span className={triggerIconVariants({ size, invalid, disabled })}>
                        {(type !== 'select' && searchString.length > 0) || label.length > 0 ? (
                          <IconButton
                            name="x"
                            label={clearSearch}
                            color="neutral"
                            variant="ghost"
                            shape="circle"
                            size="sm"
                            onClick={handleInputOnClear}
                            disabled={disabled || readOnly}
                          />
                        ) : (
                          slots.trailingAdornment
                        )}
                      </span>
                    )}
                  </span>
                  {slots.trailingAddOn && <span className={triggerTrailingAddOnVariants({ size, disabled })}>{slots.trailingAddOn}</span>}
                </span>
              </div>
            );
          },
        ),
      [],
    );

    return <AutocompleteTrigger {...props} />;
  },
});

export const AutocompleteOptionSlot = Slot<AutocompleteOptionProps & { contentRef: React.RefObject<HTMLDivElement> }>({
  displayName: 'Autocomplete.Option',
  preventOverride: true,
  renderDefault: ({
    value,
    contentRef,
    className,
    disableTextHighlighting: providedDisableTextHighlighting,
    label: providedLabel,
    description,
    disabled,
    children,
    size,
    ...props
  }) => {
    const {
      disableTextHighlighting,
      searchString,
      disableDefaultFiltering,
      highlightedIndex,
      registerOption,
      unregisterOption,
      currentValue,
      descriptionOrientation,
      optionRefs,
      options,
      handleOptionOnClick,
      handleOptionOnKeyDown,
      handleOptionOnMouseEnter,
    } = useAutocomplete();
    const label = providedLabel || (typeof children === 'string' ? children : value);
    const highlightedLabel = useAutocompleteHighlightedLabel(label, searchString, {
      disableTextHighlighting: providedDisableTextHighlighting || disableTextHighlighting,
    });
    const [index, setIndex] = useState(-1);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only want to update index when options, value, or searchString changes, not on every render
    useEffect(() => {
      const opts = Array.from(contentRef.current?.querySelectorAll('[role="option"]'));
      const idx = opts.findIndex((option) => option?.getAttribute('data-value') === value);
      setIndex(idx);
    }, [options, value, searchString]);

    const isHighlighted = highlightedIndex === index;

    const isFiltered = useMemo(() => {
      return (
        disableDefaultFiltering !== true && searchString && !label.toString().toLowerCase().includes(searchString.toLowerCase().trim())
      );
    }, [searchString, label, disableDefaultFiltering]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional, see above
    useEffect(() => {
      const option = {
        value,
        label,
        description,
        disabled,
        highlightedLabel,
      };

      if (!isFiltered) {
        registerOption(option);
      } else {
        unregisterOption(value);
      }

      return () => {
        unregisterOption(value);
      };
    }, [value, label, description, disabled, highlightedLabel, children, registerOption, unregisterOption, isFiltered]);

    if (isFiltered) {
      return null;
    }

    return (
      <div
        data-spellbook-name="autocomplete-option"
        className={cn([optionVariants({ size, disabled }), isFiltered && 'hidden', className])}
        data-disabled={disabled}
        aria-disabled={disabled}
        onClick={(e) => handleOptionOnClick(e, value)}
        onKeyDown={(e) => handleOptionOnKeyDown(e, index)}
        onMouseEnter={(e) => handleOptionOnMouseEnter(e, index!)}
        ref={(node) => {
          if (index !== null && optionRefs[index]) {
            optionRefs[index].current = node;
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="option"
        data-value={value}
        data-selected={value === currentValue}
        data-highlighted={isHighlighted}
        aria-selected={value === currentValue}
        data-index={index}
        {...props}
      >
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 overflow-hidden">
            {children && typeof children != 'string' ? (
              <>{Children.toArray(children)}</>
            ) : (
              <span className={optionLabelVariants({ descriptionOrientation })}>
                <Text variant="body-2" element="span" color="inherit" className="max-w-full shrink-0" aria-label={label}>
                  {(!disabled && highlightedLabel) || label}
                </Text>
                {description && (
                  <Text variant="caption-1" color="default-soft">
                    {description}
                  </Text>
                )}
              </span>
            )}
          </span>
        </span>
        {value === currentValue && <Icon name="check" color="primary" size="sm" />}
      </div>
    );
  },
});

export const AutocompleteContentSlot = Slot<AutocompleteContentProps>({
  displayName: 'Autocomplete.Content',
  isRenderedByDefault: true,
  renderDefault: ({ children, className, descriptionOrientation, size, ...props }) => {
    const { searchString, isLoading, disableDefaultNoResults, text = {}, options, type } = useAutocomplete();
    const { empty = 'No results found' } = text;
    const ref = useRef<HTMLDivElement>(null);

    const { slots, rest } = useSlots(children, {
      spinner: { Component: AutocompleteSpinnerSlot },
      searchOption: { Component: AutocompleteSearchOptionSlot, props: { size, contentRef: ref } },
    });

    const { orderedSlots } = useOrderedSlots(rest, {
      option: { Component: AutocompleteOptionSlot, props: { size, index: 0, contentRef: ref } },
      group: { Component: AutocompleteGroupSlot, props: { size, contentRef: ref } },
      empty: { Component: AutocompleteEmptySlot, props: { size } },
    });

    // options.size only counts registered interactive options — checking it here would swallow a
    // caller-supplied <Autocomplete.Empty> (rendered via orderedSlots) whenever results are empty.
    if (orderedSlots.length === 0 && disableDefaultNoResults === true) return null;

    return (
      <div
        data-spellbook-name="autocomplete-content"
        className={cn('sm:min-w-form-field-min w-full pb-2 max-h-76', className)}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <div className="flex px-4 items-center justify-center py-2">{slots.spinner}</div>
        ) : (
          <>
            {mapOrderedSlots(orderedSlots)}
            {options.size === 0 && disableDefaultNoResults !== true && (
              <div className="flex px-4 py-2">
                <Text variant="caption-1" color="default-soft">
                  {empty}
                </Text>
              </div>
            )}
            {searchString.length > 0 && type === 'search' ? slots.searchOption : null}
          </>
        )}
      </div>
    );
  },
});

export const AutocompleteLeadingAddOnSlot = Slot<LabelProps>({
  displayName: 'Autocomplete.LeadingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="autocomplete-leading-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export const AutocompleteLeadingAdornmentSlot = Slot<IconProps>({
  displayName: 'Autocomplete.LeadingAdornment',
  renderDefault: ({ name = 'square-dashed', className, color = 'default', size: _, ...props }) => {
    return (
      <Icon
        data-spellbook-name="autocomplete-leading-adornment"
        name={name}
        size="sm"
        color={color}
        className={cn('m-1', className)}
        {...props}
      />
    );
  },
});

export const AutocompleteTrailingAddOnSlot = Slot<LabelProps>({
  displayName: 'Autocomplete.TrailingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="autocomplete-trailing-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export const AutocompleteTrailingAdornmentSlot = Slot<
  IconProps & { open?: boolean; readOnly?: boolean; disabled?: boolean; type?: AutocompleteProps['type'] }
>({
  displayName: 'Autocomplete.TrailingAdornment',
  isRenderedByDefault: true,
  renderDefault: ({ name, color = 'inherit', className, disabled, open, readOnly, type, size: _, ...props }) => {
    const iconName = name || type === 'select' ? 'chevron-down' : 'search';

    return (
      !readOnly && (
        <Icon
          data-spellbook-name="autocomplete-trailing-adornment"
          name={iconName}
          size="sm"
          color={color}
          className={cn(trailingAdornmentIconVariants({ open, type }), className)}
          {...props}
        />
      )
    );
  },
});

export const AutocompleteSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'Autocomplete.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ color = 'primary', ...props }) => {
    return <Spinner data-spellbook-name="autocomplete-spinner" color={color} size="sm" {...props} />;
  },
});

export const AutocompleteGroupSlot = Slot<AutocompleteGroupProps & { contentRef: React.RefObject<HTMLDivElement> }>({
  displayName: 'Autocomplete.Group',
  renderDefault: ({ className, children, label, size, contentRef, ...props }) => {
    const id = useId();
    const { options } = useAutocomplete();
    const { orderedSlots } = useOrderedSlots(children, {
      option: { Component: AutocompleteOptionSlot, props: { size, contentRef } },
    });

    const shouldSimplyRegisterSlots = useMemo(() => {
      const slotValues = orderedSlots.map((slot) => (slot.component.props as { value?: string }).value) || [];
      return slotValues.length === 0 || slotValues.every((value) => !Array.from(options.values()).some((option) => option.value === value));
    }, [orderedSlots, options]);

    if (shouldSimplyRegisterSlots) {
      return mapOrderedSlots(orderedSlots);
    }

    return (
      <div
        data-spellbook-name="autocomplete-group"
        className={cn('border-t border-t-border-default pt-2 first-of-type:pt-0 pb-2 last-of-type:pb-0 first-of-type:border-0', className)}
        role="group"
        aria-labelledby={id}
        {...props}
      >
        <Text
          variant="caption-1"
          color="default-soft"
          className={cn(size === 'md' ? 'px-2' : 'px-3', 'py-2', 'flex')}
          id={id}
          role="presentation"
        >
          {label}
        </Text>
        {mapOrderedSlots(orderedSlots)}
      </div>
    );
  },
});

export const AutocompleteEmptySlot = Slot<React.ComponentPropsWithoutRef<'div'> & Pick<AutocompleteProps, 'size'>>({
  displayName: 'Autocomplete.Empty',
  renderDefault: ({ className, children, size, ...props }) => {
    return (
      <div data-spellbook-name="autocomplete-empty" className={cn(size === 'md' ? 'px-2' : 'px-3', 'py-2', 'flex', className)} {...props}>
        <Text variant="caption-1" color="default-soft">
          {children}
        </Text>
      </div>
    );
  },
});

export const AutocompleteSearchOptionSlot = Slot<
  React.ComponentPropsWithoutRef<'div'> & Pick<AutocompleteProps, 'size'> & { contentRef: React.RefObject<HTMLDivElement> }
>({
  displayName: 'Autocomplete.SearchOption',
  isRenderedByDefault: true,
  renderDefault: ({ className, children, size, contentRef, ...props }) => {
    const {
      registerOption,
      unregisterOption,
      handleOptionOnKeyDown,
      handleOptionOnMouseEnter,
      handleInputSubmit,
      optionRefs,
      searchString,
      text = {},
      disableDefaultSearchOption,
      highlightedIndex,
    } = useAutocomplete();
    const { searchLabel = 'Search' } = text;
    const [index, setIndex] = useState<number>(-1);
    const isHighlighted = highlightedIndex === index;
    const id = useId();
    const groupId = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only want to update index when optionRefs or searchString changes, not on every render
    useEffect(() => {
      const opts = contentRef.current?.querySelectorAll('[role="option"]');
      const idx = Array.from(opts).findIndex((option) => option.getAttribute('data-value') === SEARCH_OVERRIDE_VALUE);
      setIndex(idx);
    }, [optionRefs, SEARCH_OVERRIDE_VALUE]);

    useEffect(() => {
      if (!disableDefaultSearchOption) {
        registerOption({ value: SEARCH_OVERRIDE_VALUE, label: searchString });
      }

      return () => {
        if (!disableDefaultSearchOption) {
          unregisterOption(SEARCH_OVERRIDE_VALUE);
        }
      };
    }, [searchString, registerOption, unregisterOption, disableDefaultSearchOption]);

    if (disableDefaultSearchOption) {
      return null;
    }

    return (
      <div
        data-spellbook-name="autocomplete-search-option"
        role="group"
        aria-labelledby={groupId}
        className="border-t border-t-border-default pt-2 first-of-type:pt-0 first-of-type:border-0"
      >
        <span role="presentation" id={groupId} className={cn(size === 'md' ? 'px-2' : 'px-3', 'py-2', 'flex')}>
          <Text variant="caption-1" color="default-soft">
            {searchLabel}
          </Text>
        </span>
        <div
          role="option"
          id={id}
          data-selected={false}
          data-value={SEARCH_OVERRIDE_VALUE}
          data-highlighted={isHighlighted}
          aria-selected={false}
          ref={(node) => {
            if (optionRefs[index]) {
              optionRefs[index].current = node;
            }
          }}
          onClick={() => handleInputSubmit(searchString)}
          onKeyDown={(e) => handleOptionOnKeyDown(e, index!)}
          onMouseEnter={(e) => handleOptionOnMouseEnter(e, index)}
          className={cn([
            'flex',
            'cursor-pointer',
            'items-center',
            'justify-between',
            'outline-none',
            'py-2',
            'group',
            '[&:not(:focus-visible):hover]:bg-surface-overlay-1',
            'focus-visible:bg-surface-overlay-1',
            'data-[highlighted=true]:bg-surface-overlay-1',
            'gap-2',
            size === 'md' ? 'px-2' : 'px-3',
            className,
          ])}
          {...props}
          tabIndex={0}
        >
          <Icon name="search" color="default" size="sm" role="presentation" />
          <Text variant="body-2" className="flex-1 min-w-0">
            {searchString}
          </Text>
        </div>
      </div>
    );
  },
});
